export type RequiredDocumentKey =
  | "IdDocument"
  | "ApplicationDocument"
  | "PolicyDocument";

export type ComplianceCardKey =
  | RequiredDocumentKey
  | "AnyMissingDocs"
  | "ZeroDocuments"
  | "GeneratedNotSent"
  | "NoPaymentMethod"
  | "DebiCheckIncomplete"
  | "DigitalOnboardingStuck"
  | "LapseRisk"
  | "RecentlyLapsed"
  | "MissingBeneficiary"
  | "IncompleteCoveredLives"
  | "ClaimsMissingDocs"
  | "InvalidCellphone"
  | "DeductionBelowPremium"
  | "PendingLapse";

export interface RequiredDocument {
  Key: RequiredDocumentKey;
  Label: string;
}

export const REQUIRED_DOCUMENTS: RequiredDocument[] = [
  { Key: "IdDocument", Label: "ID Document" },
  { Key: "ApplicationDocument", Label: "Application Document" },
  { Key: "PolicyDocument", Label: "Policy Document" },
];

export const REQUIRED_CLAIM_DOCUMENTS = [
  { Key: "DeathCertificate", Label: "Death Certificate" },
  { Key: "IdDocumentOfDeceased", Label: "ID of Deceased" },
  { Key: "BI1663Certificate", Label: "BI-1663 Certificate" },
] as const;

export interface GroupDocumentTemplates {
  ApplicationDocument?: string | null;
  PolicyDocument?: string | null;
}

const ID_PATTERNS = [
  /iddocument/,
  /idcopy/,
  /identity/,
  /identification/,
  /greenbook/,
  /smartcard/,
  /passport/,
  /birthcertificate/,
];

const APPLICATION_PATTERNS = [/application/, /appform/, /appdoc/];
const POLICY_PATTERNS = [/policy/, /schedule/, /welcomeletter/];

const DIGITAL_SMS_ACTIONS = new Set([
  "APPLICATION_SMS_SENT",
  "NEW_POLICY_SMS_SENT",
  "DOCUMENT_SMS_SENT",
  "POLICY_SMS_SENT",
  "SMS_SENT",
  "ApplicationSmsSent",
  "NewPolicySmsSent",
  "DocumentSmsSent",
]);

const collapse = (name?: string) =>
  String(name ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const tokenise = (name?: string) =>
  String(name ?? "")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

const REQUIRED_DOCUMENT_TYPE_KEYS = new Set<string>([
  "IdDocument",
  "ApplicationDocument",
  "PolicyDocument",
]);

const classifyByDocumentType = (document: any): RequiredDocumentKey | null => {
  const raw =
    document?.DocumentType ??
    document?.documentType ??
    document?.Type ??
    document?.DocumentKey;
  if (raw == null || raw === "") return null;
  const key = String(raw).trim();
  if (REQUIRED_DOCUMENT_TYPE_KEYS.has(key)) {
    return key as RequiredDocumentKey;
  }
  return null;
};

const classifyDocumentMetadata = (
  document: any,
  templates?: GroupDocumentTemplates
): RequiredDocumentKey | null => {
  const byType = classifyByDocumentType(document);
  if (byType) return byType;

  const templateId = document?.DocumentID;
  if (templateId) {
    if (templateId === templates?.ApplicationDocument) return "ApplicationDocument";
    if (templateId === templates?.PolicyDocument) return "PolicyDocument";
  }

  const name = collapse(document?.Name);
  const tokens = tokenise(document?.Name);

  if (APPLICATION_PATTERNS.some((pattern) => pattern.test(name))) {
    return "ApplicationDocument";
  }
  if (ID_PATTERNS.some((pattern) => pattern.test(name)) || tokens.includes("id")) {
    return "IdDocument";
  }
  if (POLICY_PATTERNS.some((pattern) => pattern.test(name))) {
    return "PolicyDocument";
  }

  return null;
};

export const classifyDocument = (
  document: any,
  templates?: GroupDocumentTemplates
): RequiredDocumentKey | null => {
  if (!hasValidDocumentLink(document)) return null;
  return classifyDocumentMetadata(document, templates);
};

export const getSuppliedDocumentKeys = (
  documents: any,
  templates?: GroupDocumentTemplates
): Set<RequiredDocumentKey> => {
  const supplied = new Set<RequiredDocumentKey>();
  (Array.isArray(documents) ? documents : []).forEach((document: any) => {
    const key = classifyDocument(document, templates);
    if (key) supplied.add(key);
  });
  return supplied;
};

export const getMissingDocumentKeys = (
  documents: any,
  templates?: GroupDocumentTemplates
): RequiredDocumentKey[] => {
  const supplied = getSuppliedDocumentKeys(documents, templates);
  return REQUIRED_DOCUMENTS.filter((required) => !supplied.has(required.Key)).map(
    (required) => required.Key
  );
};

export const getDocumentLabel = (key: RequiredDocumentKey): string =>
  REQUIRED_DOCUMENTS.find((required) => required.Key === key)?.Label ?? key;

export const hasLinkedDocuments = (documents: any): boolean =>
  (Array.isArray(documents) ? documents : []).some((document: any) => !!document?.Link);

/** Boolean DocumentSent only (BE sets true after successful SMS/email). */
export const isDocumentSent = (document: any): boolean => {
  const v = document?.DocumentSent;
  return v === true || v === 1;
};

export const getGeneratedNotSentDocuments = (documents: any): any[] =>
  (Array.isArray(documents) ? documents : []).filter(
    (document: any) =>
      document?.DocumentGenerated === true && !isDocumentSent(document)
  );

export const hasValidDocumentLink = (document: any): boolean => {
  const link = document?.Link;
  if (link == null) return false;
  const trimmed = String(link).trim();
  return (
    trimmed.length > 0 &&
    trimmed.toLowerCase() !== "null" &&
    trimmed.toLowerCase() !== "undefined"
  );
};

export const getSendableMemberDocuments = (documents: any): any[] =>
  getGeneratedNotSentDocuments(documents).filter((document: any) =>
    hasValidDocumentLink(document)
  );

export const getGeneratedDocumentsMissingLink = (documents: any): any[] =>
  getGeneratedNotSentDocuments(documents).filter(
    (document: any) => !hasValidDocumentLink(document)
  );

export interface DocumentSendValidationResult {
  isValid: boolean;
  message?: string;
  invalidNames?: string[];
}

export const validateSelectedDocumentsForSend = (
  selectedIds: string[] | null | undefined,
  allDocs: any
): DocumentSendValidationResult => {
  const ids = Array.isArray(selectedIds) ? selectedIds : [];
  if (ids.length === 0) {
    return {
      isValid: false,
      message: "Please select at least one document to send.",
    };
  }

  const docs = Array.isArray(allDocs) ? allDocs : [];
  const invalidNames: string[] = [];

  ids.forEach((id) => {
    const doc = docs.find((item: any) => String(item?.id) === String(id));
    if (!doc || !hasValidDocumentLink(doc)) {
      invalidNames.push(doc?.Name ?? `Document ${id}`);
    }
  });

  if (invalidNames.length > 0) {
    return {
      isValid: false,
      message: `Cannot send — missing document link for: ${invalidNames.join(", ")}. Regenerate the document first.`,
      invalidNames,
    };
  }

  return { isValid: true };
};

export const getGroupDocumentTemplates = (group: any): GroupDocumentTemplates => ({
  ApplicationDocument: group?.ApplicationDocument ?? null,
  PolicyDocument: group?.PolicyDocument ?? null,
});

export const getSendableApplicationDocument = (
  documents: any,
  templates?: GroupDocumentTemplates
): any | null => {
  const sendable = getSendableMemberDocuments(documents);
  return (
    sendable.find(
      (doc) => classifyDocument(doc, templates) === "ApplicationDocument"
    ) ?? null
  );
};

export const getApplicationSendBlockReason = (
  documents: any,
  group: any
): string | null => {
  if (!group?.ApplicationDocument) {
    return "Application document is not assigned for this group. Configure it under Group → Communication → Document Assignment & Rules.";
  }

  const templates = getGroupDocumentTemplates(group);
  const generatedMissingLink = getGeneratedDocumentsMissingLink(documents).filter(
    (doc) => classifyDocumentMetadata(doc, templates) === "ApplicationDocument"
  );

  if (generatedMissingLink.length > 0) {
    const names = generatedMissingLink.map((doc) => doc?.Name ?? "Application document").join(", ");
    return `Application document has no download link (${names}). Regenerate it before sending.`;
  }

  if (!getSendableApplicationDocument(documents, templates)) {
    return "No generated application document with a valid link is available. Generate the application document first.";
  }

  return null;
};

export const getPaymentMethods = (member: any): any[] =>
  [member?.PaymentType, member?.SecondaryPaymentType].filter(Boolean);

export const hasPaymentMethod = (member: any): boolean =>
  getPaymentMethods(member).length > 0;

const isBlank = (value: any) =>
  value === null || value === undefined || String(value).trim() === "";

const DEBI_CHECK_ERROR_STATUSES = new Set([
  "rejected",
  "failed",
  "error",
  "declined",
  "cancelled",
  "canceled",
]);

export type DebiCheckSeverity = "error" | "pending" | "incomplete";

export const isDebiCheckErrorPayment = (payment: any): boolean => {
  if (!payment || String(payment?.Type) !== "DebiCheck") return false;

  const status = String(payment?.MandateStatus ?? "").toLowerCase().trim();
  const instruction = String(payment?.MandateInstructionStatus ?? "")
    .toLowerCase()
    .trim();

  if (DEBI_CHECK_ERROR_STATUSES.has(status) || DEBI_CHECK_ERROR_STATUSES.has(instruction)) {
    return true;
  }

  const accepted =
    payment?.MandateAccepted === true || instruction === "accepted";
  const processed =
    payment?.MandateProcessed === true ||
    payment?.MandateProcessed === 1 ||
    String(payment?.MandateProcessed).toLowerCase() === "true";

  // Processed but not accepted → treat as error/rejected
  if (processed && !accepted && status !== "active" && status !== "successful") {
    return true;
  }

  return false;
};

export const getDebiCheckSeverity = (
  payment: any
): DebiCheckSeverity | null => {
  if (!payment || String(payment?.Type) !== "DebiCheck") return null;

  if (isDebiCheckErrorPayment(payment)) return "error";

  const status = String(payment?.MandateStatus ?? "").toLowerCase();
  if (["pending", "updating", "suspended"].includes(status) || payment?.MandateProcessed === false) {
    return "pending";
  }

  const accepted =
    payment?.MandateAccepted === true ||
    String(payment?.MandateInstructionStatus ?? "").toLowerCase() === "accepted";
  const statusOk = status === "active" || status === "successful";
  const missingBanking =
    isBlank(payment?.AccountNumber) ||
    isBlank(payment?.Bank) ||
    isBlank(payment?.AccountType) ||
    isBlank(payment?.BranchCode);

  if (missingBanking || (!accepted && !statusOk)) return "incomplete";
  return null;
};

/** Worst severity across DebiCheck payment methods on a member. */
export const getDebiCheckIssueForMember = (
  member: any
): { severity: DebiCheckSeverity; detail: string } | null => {
  const methods = getPaymentMethods(member).filter(
    (payment) => String(payment?.Type) === "DebiCheck"
  );
  if (methods.length === 0) return null;

  let worst: DebiCheckSeverity | null = null;
  const reasons: string[] = [];

  const rank = (s: DebiCheckSeverity) =>
    s === "error" ? 3 : s === "pending" ? 2 : 1;

  methods.forEach((payment) => {
    const severity = getDebiCheckSeverity(payment);
    if (!severity) return;
    if (!worst || rank(severity) > rank(worst)) worst = severity;

    const status = payment?.MandateStatus || payment?.MandateInstructionStatus;
    if (severity === "error") {
      reasons.push(
        status ? `DebiCheck error: ${status}` : "DebiCheck mandate rejected/failed"
      );
    } else if (severity === "pending") {
      reasons.push(status ? `DebiCheck pending: ${status}` : "DebiCheck mandate pending");
    } else {
      reasons.push("DebiCheck incomplete (banking or acceptance)");
    }
  });

  if (!worst) return null;
  return {
    severity: worst,
    detail: reasons[0] || `DebiCheck ${worst}`,
  };
};

export const isDebiCheckIncomplete = (member: any): boolean => {
  const methods = getPaymentMethods(member).filter(
    (payment) => String(payment?.Type) === "DebiCheck"
  );
  if (methods.length === 0) return false;

  return methods.some((payment) => getDebiCheckSeverity(payment) != null);
};

export const isDigitalOnboardingStuck = (member: any): boolean => {
  if (member?.Signature) return false;

  const hasOtp = !isBlank(member?.OTP);
  const audits = Array.isArray(member?.AuditTrail) ? member.AuditTrail : [];
  const smsSent = audits.some((audit: any) =>
    DIGITAL_SMS_ACTIONS.has(String(audit?.Action ?? ""))
  );

  return hasOtp || smsSent;
};

export const parsePremium = (value: any): number => {
  const parsed = Number(String(value ?? "0").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(parsed) ? Math.abs(parsed) : 0;
};

/** Policy premium floor: product premium + covered-life extended product premiums (Member Management card). */
export const getPolicyPremiumFloor = (member: any): number => {
  let total = parsePremium(member?.Policy?.Product?.PolicyPremium);
  const coveredLives = Array.isArray(member?.CoveredLive)
    ? member.CoveredLive
    : [];
  coveredLives.forEach((cl: any) => {
    if (cl?.ExtendedProduct?.PolicyPremium != null) {
      total += parsePremium(cl.ExtendedProduct.PolicyPremium);
    }
  });
  return total;
};

/** Total deduction (TotalPremium) is lower than the policy premium floor. */
export const isDeductionBelowPremium = (member: any): boolean => {
  const floor = getPolicyPremiumFloor(member);
  if (floor <= 0) return false;
  return parsePremium(member?.TotalPremium) < floor;
};

export const getOutstandingPremium = (member: any): number => {
  const raw = member?.Policy?.OutstandingPremium;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Months behind ≈ |min(outstanding, 0)| / monthly premium.
 * Outstanding is typically negative when the member owes.
 */
export const getMonthsBehind = (
  outstanding: number,
  premium: number
): number | null => {
  if (!(premium > 0)) return null;
  const behind = Math.abs(Math.min(outstanding, 0));
  if (behind <= 0) return 0;
  return Math.round((behind / premium) * 10) / 10;
};

export const getMonthsBehindForMember = (member: any): number | null => {
  const premium = parsePremium(member?.TotalPremium);
  const outstanding = getOutstandingPremium(member);
  return getMonthsBehind(outstanding, premium);
};

export const formatMonthsBehind = (months: number | null | undefined): string => {
  if (months == null || !Number.isFinite(months)) return "-";
  if (months === 0) return "0";
  const label = months === 1 ? "month" : "months";
  return `~${months} ${label}`;
};

/** One unpaid month: outstanding <= -premium. Auto-lapse starts at about -2 * premium. */
export const isLapseRisk = (member: any): boolean => {
  const premium = parsePremium(member?.TotalPremium);
  if (premium <= 0) return false;
  const outstanding = getOutstandingPremium(member);
  return outstanding <= -premium && outstanding > -(premium * 2);
};

export const parseDate = (value: any): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const daysSince = (value: any, now = new Date()): number | null => {
  const date = parseDate(value);
  if (!date) return null;
  const ms = now.getTime() - date.getTime();
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
};

export const isRecentlyLapsed = (
  member: any,
  withinDays = 90,
  now = new Date()
): boolean => {
  const lapsed =
    member?.PolicyLapsed === true ||
    member?.PolicyLapsed === 1 ||
    String(member?.PolicyLapsed).toLowerCase() === "true";
  if (!lapsed) return false;

  const age = daysSince(member?.LapsedDate, now);
  if (age === null) return true;
  return age <= withinDays;
};

export const isMissingBeneficiary = (member: any): boolean => !member?.Beneficiary;

export const isIncompleteCoveredLives = (member: any): boolean => {
  const lives = Array.isArray(member?.CoveredLive) ? member.CoveredLive : [];
  if (lives.length === 0) return true;

  const hasMain = lives.some(
    (life: any) => String(life?.CoveredType ?? "").toLowerCase() === "main"
  );
  if (!hasMain) return true;

  return lives.some(
    (life: any) => isBlank(life?.CoveredIdNumber) || isBlank(life?.CoveredDateOfBirth)
  );
};

export const getMissingClaimDocumentKeys = (claim: any): string[] => {
  const docs = Array.isArray(claim?.ClaimDocument) ? claim.ClaimDocument : [];
  const present = new Set(
    docs
      .filter((doc: any) => !!doc?.Link || !!doc?.Name)
      .map((doc: any) => collapse(doc?.Name))
  );

  return REQUIRED_CLAIM_DOCUMENTS.filter((required) => {
    const key = collapse(required.Key);
    const label = collapse(required.Label);
    return ![...present].some(
      (name) => name.includes(key) || name.includes(label) || key.includes(name)
    );
  }).map((required) => required.Key);
};

export const COMPLIANCE_CARD_META: Record<
  ComplianceCardKey,
  { Label: string; Description: string; Entity: "member" | "claim" }
> = {
  IdDocument: {
    Label: "ID Document",
    Description: "Members missing an ID document",
    Entity: "member",
  },
  ApplicationDocument: {
    Label: "Application Document",
    Description: "Members missing an application document",
    Entity: "member",
  },
  PolicyDocument: {
    Label: "Policy Document",
    Description: "Members missing a policy document",
    Entity: "member",
  },
  AnyMissingDocs: {
    Label: "Any Missing Documents",
    Description: "Members missing at least one required policy document",
    Entity: "member",
  },
  ZeroDocuments: {
    Label: "No Documents At All",
    Description: "Members with zero uploaded or generated documents",
    Entity: "member",
  },
  GeneratedNotSent: {
    Label: "Generated But Not Sent",
    Description: "Documents generated but not yet sent to the member",
    Entity: "member",
  },
  NoPaymentMethod: {
    Label: "No Payment Method",
    Description: "Active members without a payment method",
    Entity: "member",
  },
  DebiCheckIncomplete: {
    Label: "DebiCheck Incomplete",
    Description: "DebiCheck selected but mandate/banking is incomplete",
    Entity: "member",
  },
  DigitalOnboardingStuck: {
    Label: "Digital Onboarding Stuck",
    Description: "Digital application started but signature not completed",
    Entity: "member",
  },
  LapseRisk: {
    Label: "At Risk Of Lapsing",
    Description: "About one unpaid premium — approaching auto-lapse",
    Entity: "member",
  },
  RecentlyLapsed: {
    Label: "Recently Lapsed",
    Description: "Policies lapsed in the last 90 days",
    Entity: "member",
  },
  MissingBeneficiary: {
    Label: "Missing Beneficiary",
    Description: "Active members with no beneficiary on file",
    Entity: "member",
  },
  IncompleteCoveredLives: {
    Label: "Incomplete Covered Lives",
    Description: "Covered lives missing ID number or date of birth",
    Entity: "member",
  },
  ClaimsMissingDocs: {
    Label: "Claims Missing Documents",
    Description: "Claims missing death certificate, deceased ID, or BI-1663",
    Entity: "claim",
  },
  InvalidCellphone: {
    Label: "Invalid Cellphone Numbers",
    Description: "Members with missing or invalid SA cellphone numbers",
    Entity: "member",
  },
  DeductionBelowPremium: {
    Label: "Deduction Below Policy Premium",
    Description:
      "Members whose total deduction is lower than the policy premium",
    Entity: "member",
  },
  PendingLapse: {
    Label: "Pending Lapse Approval",
    Description:
      "Lapse runs awaiting Admin / Super Admin review before policies are lapsed",
    Entity: "member",
  },
};
